import axios from 'axios'
import store from '../store'
import router from '../router'
import { notifyError } from './notifications'

const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: 60000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }
})

// Attach auth token and track loading
api.interceptors.request.use(config => {
  if (!config.silent) store.dispatch('loading/start')
  const token = store.getters['auth/token']
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Unwrap response data, handle 401, detect connection loss
api.interceptors.response.use(
  response => {
    if (!response.config?.silent) store.dispatch('loading/stop')
    if (!store.getters['connection/isOnline']) {
      store.dispatch('connection/setOnline')
    }
    return response.data
  },
  error => {
    if (!error.config?.silent) store.dispatch('loading/stop')

    // Network error or timeout — no response from server. But a
    // client-side AbortController.abort() also produces an error with
    // no `response` (a CanceledError), and that is NOT a connectivity
    // problem — it's a deliberate teardown (e.g. analytics views
    // aborting in-flight panel fetches when the user navigates away).
    // Flipping the connection state to offline in that case surfaces
    // the "Connection Lost" modal on every route change.
    if (!error.response) {
      const canceled = axios.isCancel(error) ||
        error.code === 'ERR_CANCELED' ||
        error.name === 'CanceledError' ||
        error.name === 'AbortError'
      if (!canceled) {
        store.dispatch('connection/setOffline')
      }
      return Promise.reject(error)
    }

    // Got a response — server is reachable
    if (!store.getters['connection/isOnline']) {
      store.dispatch('connection/setOnline')
    }

    if (error.response.status === 401 && router.currentRoute.value.path !== '/login') {
      store.dispatch('auth/logout')
      router.push('/login')
    } else if (!error.config?.silent) {
      const message = error.response.data?.message || error.response.statusText || 'Something went wrong'
      notifyError(message)
    }

    return Promise.reject(error.response.data || error)
  }
)

export default api
